import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { uploadImage } from '../firebase/config';
import Layout from '../components/Layout';
import SignInModal from '../components/SignInModal';
import SEO from '../components/SEO';
import { 
  ArrowLeft, Loader2, RotateCcw, ShoppingCart, 
  Upload, X, Save, Minus, Plus, FileSpreadsheet, Download, Eye, Share2, Copy, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_SHAPES = [
  { value: 'circle', label: 'Round' },
  { value: 'square', label: 'Square' },
  { value: 'rounded-rect', label: 'Rounded' },
];

const getLogoShapeStyle = (shape) => {
  switch (shape) {
    case 'square': return { borderRadius: '12%' };
    case 'rounded-rect': return { borderRadius: '16%' };
    case 'circle': default: return { borderRadius: '50%' };
  }
};

// Helper function to normalize text elements - ensure textAlign defaults to 'center'
const normalizeTextElements = (elements) => {
  if (!elements || !Array.isArray(elements)) return [];
  return elements.map(el => ({
    ...el,
    textAlign: el.textAlign || 'center' // Default to center alignment
  }));
};

const UserTemplateEditor = () => {
  const { templateId, categorySlug, templateSlug } = useParams();
  const [searchParams] = useSearchParams();
  const designId = searchParams.get('design_id');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { loadCart: refreshGlobalCart } = useCart();
  const canvasRef = useRef(null);
  const frontLogoInputRef = useRef(null);
  const backLogoInputRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isEditorMode, setIsEditorMode] = useState(false); // Preview mode by default
  
  const [activeSide, setActiveSide] = useState('front');
  
  const [frontTextElements, setFrontTextElements] = useState([]);
  const [backTextElements, setBackTextElements] = useState([]);
  
  // Independent logo states for front and back
  const [frontUserLogo, setFrontUserLogo] = useState(null);
  const [backUserLogo, setBackUserLogo] = useState(null);
  const [frontLogoPosition, setFrontLogoPosition] = useState(null);
  const [backLogoPosition, setBackLogoPosition] = useState(null);
  
  const [editingElement, setEditingElement] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const pendingActionRef = useRef(null);
  
  // QR position state (user can move the QR code)
  const [qrPosition, setQrPosition] = useState(null);
  
  // Order quantity and bulk options
  const [quantity, setQuantity] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]); // Parsed CSV rows
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [globalPricing, setGlobalPricing] = useState(null);
  const csvInputRef = useRef(null);
  
  // Paper card quantity for paper_only orders
  const [paperQuantity, setPaperQuantity] = useState(100);
  
  // Order type: NFC only (paper options removed)
  const [orderType, setOrderType] = useState('nfc_only');
  
  // Quick share state
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Drag and resize states
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCorner, setResizeCorner] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, logoX: 0, logoY: 0, logoSize: 0 });
  
  // Text dragging state
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [draggingTextId, setDraggingTextId] = useState(null);
  const textDragStartRef = useRef({ x: 0, y: 0, elX: 0, elY: 0 });
  
  // QR dragging state
  const [isDraggingQr, setIsDraggingQr] = useState(false);
  const qrDragStartRef = useRef({ x: 0, y: 0, qrX: 0, qrY: 0 });
  
  // Swipe detection state
  const swipeStartRef = useRef({ x: 0, y: 0, time: 0 });
  
  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved'
  const autoSaveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);

  const textElements = activeSide === 'front' ? frontTextElements : backTextElements;
  const setTextElements = activeSide === 'front' ? setFrontTextElements : setBackTextElements;

  // Swipe handlers for switching front/back
  const handleSwipeStart = useCallback((e) => {
    // Don't capture swipe if dragging logo or resizing or dragging text or QR
    if (isDragging || isResizing || isDraggingText || isDraggingQr) return;
    const touch = e.touches?.[0];
    if (touch) {
      swipeStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    }
  }, [isDragging, isResizing, isDraggingText, isDraggingQr]);

  const handleSwipeEnd = useCallback((e) => {
    if (isDragging || isResizing || isDraggingText || isDraggingQr) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const deltaTime = Date.now() - swipeStartRef.current.time;
    
    // Only count as swipe if: horizontal movement > 50px, vertical < 100px, time < 500ms
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 100 && deltaTime < 500) {
      if (deltaX < 0 && activeSide === 'front') {
        // Swipe left -> show back
        setActiveSide('back');
        setEditingElement(null);
      } else if (deltaX > 0 && activeSide === 'back') {
        // Swipe right -> show front
        setActiveSide('front');
        setEditingElement(null);
      }
    }
  }, [isDragging, isResizing, activeSide, isDraggingQr]);

  // Scale font sizes proportionally to canvas width (reference: 500px desktop width)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setCanvasScale(Math.min(1, w / 500));
      }
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [template]);

  // Fetch discount tiers and global pricing
  useEffect(() => {
    const fetchDiscountTiers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/shop/quantity-discounts`);
        if (res.ok) {
          const data = await res.json();
          setDiscountTiers(data);
        }
      } catch (e) { console.error('Failed to fetch discount tiers'); }
    };
    const fetchGlobalPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/shop/global-pricing`);
        if (res.ok) {
          const data = await res.json();
          setGlobalPricing(data);
        }
      } catch (e) { console.error('Failed to fetch global pricing'); }
    };
    fetchDiscountTiers();
    fetchGlobalPricing();
  }, []);

  // Auto-save effect - triggers 2 seconds after any design change
  useEffect(() => {
    if (!user || !template) return;
    
    // Create a snapshot of current state to compare
    const currentState = JSON.stringify({
      frontTextElements,
      backTextElements,
      frontUserLogo,
      backUserLogo,
      frontLogoPosition,
      backLogoPosition
    });
    
    // Skip if nothing changed since last save
    if (lastSavedRef.current === currentState) return;
    
    // Clear previous timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (2 seconds debounce)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        const token = await user.getIdToken();
        const tid = template?.id || templateId;
        
        const response = await fetch(`${API_URL}/api/card-designer/my-designs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ 
            template_id: tid, 
            front_text_elements: frontTextElements, 
            back_text_elements: backTextElements, 
            front_user_logo: frontUserLogo,
            back_user_logo: backUserLogo,
            front_logo_position: frontLogoPosition,
            back_logo_position: backLogoPosition,
            user_logo: frontUserLogo,
            user_logo_position: frontLogoPosition
          })
        });
        
        if (response.ok) {
          lastSavedRef.current = currentState;
          setAutoSaveStatus('saved');
          // Clear "saved" status after 2 seconds
          setTimeout(() => setAutoSaveStatus(''), 2000);
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        setAutoSaveStatus('');
      }
    }, 2000);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [user, template, templateId, frontTextElements, backTextElements, frontUserLogo, backUserLogo, frontLogoPosition, backLogoPosition]);

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoading(true);
        let url;
        if (templateId) {
          url = `${API_URL}/api/card-designer/templates/${templateId}`;
        } else if (categorySlug && templateSlug) {
          url = `${API_URL}/api/card-designer/templates/by-slug/${categorySlug}/${templateSlug}`;
        } else {
          throw new Error('No template identifier');
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error('Template not found');
        const data = await response.json();
        setTemplate(data);

        // If design_id is present, load saved design data
        if (designId && user) {
          try {
            const token = await user.getIdToken();
            const dRes = await fetch(`${API_URL}/api/card-designer/my-designs/${designId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (dRes.ok) {
              const savedDesign = await dRes.json();
              setFrontTextElements(normalizeTextElements(savedDesign.front_text_elements?.length ? savedDesign.front_text_elements : (data.front_text_elements || [])));
              setBackTextElements(normalizeTextElements(savedDesign.back_text_elements?.length ? savedDesign.back_text_elements : (data.back_text_elements || [])));
              if (savedDesign.front_user_logo) setFrontUserLogo(savedDesign.front_user_logo);
              if (savedDesign.back_user_logo) setBackUserLogo(savedDesign.back_user_logo);
              if (savedDesign.front_logo_position) setFrontLogoPosition(savedDesign.front_logo_position);
              if (savedDesign.back_logo_position) setBackLogoPosition(savedDesign.back_logo_position);
              // Load QR position from saved design or fall back to template
              if (savedDesign.qr_position) setQrPosition(savedDesign.qr_position);
              else if (data.back_qr_position) setQrPosition({ ...data.back_qr_position });
              // Legacy support
              if (savedDesign.user_logo && !savedDesign.front_user_logo) setFrontUserLogo(savedDesign.user_logo);
              if (savedDesign.user_logo_position && !savedDesign.front_logo_position) setFrontLogoPosition(savedDesign.user_logo_position);
              return;
            }
          } catch (e) { console.warn('Could not load saved design, using template defaults'); }
        }

        // Default: use template data
        setFrontTextElements(normalizeTextElements(data.front_text_elements?.length ? data.front_text_elements : (data.default_text_elements || [])));
        setBackTextElements(normalizeTextElements(data.back_text_elements?.length ? data.back_text_elements : []));
        // Initialize QR position from template
        if (data.back_qr_position) setQrPosition({ ...data.back_qr_position });
      } catch (error) {
        toast({ title: "Error", description: "Failed to load template", variant: "destructive" });
        navigate('/shop');
      } finally { setLoading(false); }
    };
    loadTemplate();
  }, [templateId, categorySlug, templateSlug, designId, user, navigate, toast]);

  // Click-to-edit handler
  const handleTextClick = useCallback((e, id) => {
    e.stopPropagation();
    setEditingElement(id);
  }, []);

  const handleTextBlur = (id, newText) => {
    if (newText.trim()) updateTextContent(id, newText.trim());
    setEditingElement(null);
  };

  const updateTextContent = (id, newText) => {
    setTextElements(prev => prev.map(el => el.id === id ? { ...el, text: newText } : el));
  };

  const handleLogoUpload = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 15MB", variant: "destructive" }); return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (side === 'front') setFrontUserLogo(ev.target.result);
      else setBackUserLogo(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Get effective logo position for a side
  const getEffectiveLogoPosition = (side) => {
    const templateLogoPos = side === 'front' 
      ? (template?.front_logo_position || template?.logo_placeholder || { x: 50, y: 15, size: 18 })
      : template?.back_logo_position;
    
    if (!templateLogoPos) return null;
    
    const userPos = side === 'front' ? frontLogoPosition : backLogoPosition;
    
    return {
      x: userPos?.x ?? templateLogoPos.x,
      y: userPos?.y ?? templateLogoPos.y,
      size: userPos?.size ?? templateLogoPos.size,
      shape: userPos?.shape ?? templateLogoPos.shape ?? 'circle',
    };
  };

  // Drag start handler
  const handleDragStart = useCallback((e, side) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getEffectiveLogoPosition(side);
    if (!pos) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    dragStartRef.current = { x: clientX, y: clientY, logoX: pos.x, logoY: pos.y, logoSize: pos.size };
    setIsDragging(true);
  }, [template, frontLogoPosition, backLogoPosition]);

  // Resize start handler
  const handleResizeStart = useCallback((e, corner, side) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getEffectiveLogoPosition(side);
    if (!pos) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    dragStartRef.current = { x: clientX, y: clientY, logoX: pos.x, logoY: pos.y, logoSize: pos.size };
    setIsResizing(true);
    setResizeCorner(corner);
  }, [template, frontLogoPosition, backLogoPosition]);

  // Mouse/touch move handler
  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing && !isDraggingText && !isDraggingQr) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    if (isDraggingText && draggingTextId) {
      // Handle text element dragging
      const deltaX = ((clientX - textDragStartRef.current.x) / rect.width) * 100;
      const deltaY = ((clientY - textDragStartRef.current.y) / rect.height) * 100;
      const newX = Math.max(5, Math.min(95, textDragStartRef.current.elX + deltaX));
      const newY = Math.max(5, Math.min(95, textDragStartRef.current.elY + deltaY));
      setTextElements(prev => prev.map(el => el.id === draggingTextId ? { ...el, x: newX, y: newY } : el));
      return;
    }
    
    if (isDraggingQr) {
      // Handle QR code dragging
      const deltaX = ((clientX - qrDragStartRef.current.x) / rect.width) * 100;
      const deltaY = ((clientY - qrDragStartRef.current.y) / rect.height) * 100;
      const newX = Math.max(10, Math.min(90, qrDragStartRef.current.qrX + deltaX));
      const newY = Math.max(10, Math.min(90, qrDragStartRef.current.qrY + deltaY));
      setQrPosition(prev => ({ ...prev, x: newX, y: newY }));
      return;
    }
    
    const setLogoPos = activeSide === 'front' ? setFrontLogoPosition : setBackLogoPosition;
    const currentPos = getEffectiveLogoPosition(activeSide);
    
    if (isDragging) {
      const deltaX = ((clientX - dragStartRef.current.x) / rect.width) * 100;
      const deltaY = ((clientY - dragStartRef.current.y) / rect.height) * 100;
      const newX = Math.max(10, Math.min(90, dragStartRef.current.logoX + deltaX));
      const newY = Math.max(10, Math.min(90, dragStartRef.current.logoY + deltaY));
      setLogoPos(prev => ({ ...currentPos, ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      // Calculate size change based on drag distance
      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;
      const delta = (resizeCorner?.includes('r') ? deltaX : -deltaX) + (resizeCorner?.includes('b') ? deltaY : -deltaY);
      const sizeDelta = (delta / rect.width) * 100;
      const newSize = Math.max(8, Math.min(40, dragStartRef.current.logoSize + sizeDelta));
      setLogoPos(prev => ({ ...currentPos, ...prev, size: newSize }));
    }
  }, [isDragging, isResizing, isDraggingText, isDraggingQr, draggingTextId, activeSide, resizeCorner, setTextElements]);

  // Mouse/touch end handler
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeCorner(null);
    setIsDraggingText(false);
    setDraggingTextId(null);
    setIsDraggingQr(false);
  }, []);

  // QR drag start handler
  const handleQrDragStart = useCallback((e) => {
    if (!qrPosition) return;
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    qrDragStartRef.current = { x: clientX, y: clientY, qrX: qrPosition.x, qrY: qrPosition.y };
    setIsDraggingQr(true);
  }, [qrPosition]);

  // Text drag start handler - All text elements are now movable
  const handleTextDragStart = useCallback((e, el) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    textDragStartRef.current = { x: clientX, y: clientY, elX: el.x, elY: el.y };
    setIsDraggingText(true);
    setDraggingTextId(el.id);
  }, []);

  const resetToDefault = () => {
    if (!template) return;
    
    // Reset both sides' text elements to template defaults (with center alignment)
    setFrontTextElements(normalizeTextElements(template.front_text_elements?.length 
      ? JSON.parse(JSON.stringify(template.front_text_elements)) 
      : (template.default_text_elements ? JSON.parse(JSON.stringify(template.default_text_elements)) : [])));
    setBackTextElements(normalizeTextElements(template.back_text_elements?.length 
      ? JSON.parse(JSON.stringify(template.back_text_elements)) 
      : []));
    
    // Reset logos to template defaults
    setFrontUserLogo(null);
    setBackUserLogo(null);
    setFrontLogoPosition(null);
    setBackLogoPosition(null);
    
    // Reset QR position to template default
    if (template.back_qr_position) {
      setQrPosition({ ...template.back_qr_position });
    }
    
    setEditingElement(null);
    toast({ title: "Template reset", description: "All changes have been reverted to original design" });
  };

  const resetLogoToDefault = (side) => {
    if (side === 'front') {
      setFrontLogoPosition(null);
      setFrontUserLogo(null);
    } else {
      setBackLogoPosition(null);
      setBackUserLogo(null);
    }
  };

  // Calculate pricing with bulk discounts and paper card options
  const calculatePricing = useCallback(() => {
    // Use global pricing if available, otherwise fallback to template price
    const nfcPrice = globalPricing?.nfc_price || template?.price || 999;
    let discountPercent = 0;
    let tierName = '1 card';
    
    for (const tier of discountTiers) {
      const minQ = tier.min_quantity;
      const maxQ = tier.max_quantity;
      if (maxQ === null) {
        if (quantity >= minQ) {
          discountPercent = tier.discount_percent;
          tierName = `${minQ}+`;
        }
      } else if (quantity >= minQ && quantity <= maxQ) {
        discountPercent = tier.discount_percent;
        tierName = `${minQ}-${maxQ}`;
        break;
      }
    }

    // Get paper card price based on quantity from global pricing
    const getPaperPrice = (qty) => {
      if (!globalPricing) return 499; // Default
      switch(qty) {
        case 100: return globalPricing.paper_100 || 499;
        case 200: return globalPricing.paper_200 || 899;
        case 300: return globalPricing.paper_300 || 1199;
        case 500: return globalPricing.paper_500 || 1799;
        case 1000: return globalPricing.paper_1000 || 2999;
        default: return globalPricing.paper_100 || 499;
      }
    };
    
    // Paper cards multiply with quantity (e.g., 2 sets × 500 cards = 1000 total cards)
    const totalPaperCards = paperQuantity * quantity;
    const paperPricePerSet = getPaperPrice(paperQuantity);
    
    if (orderType === 'paper_only') {
      // Paper cards only - price per set multiplied by quantity
      const paperTotal = paperPricePerSet * quantity;
      return { 
        basePrice: paperPricePerSet, 
        quantity: quantity, 
        subtotal: paperTotal, 
        discountPercent: 0, 
        discountAmount: 0, 
        nfcPrice: 0,
        paperPrice: paperTotal,
        total: paperTotal, 
        tierName: `${quantity} set(s)`,
        orderType: 'paper_only',
        paperQuantity,
        totalPaperCards
      };
    } else if (orderType === 'nfc_only') {
      // NFC only - global NFC price with bulk discount
      const nfcSubtotal = nfcPrice * quantity;
      const discountAmount = Math.round(nfcSubtotal * (discountPercent / 100));
      const total = nfcSubtotal - discountAmount;
      return { 
        basePrice: nfcPrice, 
        quantity, 
        subtotal: nfcSubtotal, 
        discountPercent, 
        discountAmount, 
        nfcPrice: total,
        paperPrice: 0,
        total, 
        tierName,
        orderType: 'nfc_only',
        totalPaperCards: 0
      };
    } else {
      // NFC + Paper - both prices, paper multiplied by quantity
      const nfcSubtotal = nfcPrice * quantity;
      const paperTotal = paperPricePerSet * quantity;
      const nfcDiscountAmount = Math.round(nfcSubtotal * (discountPercent / 100));
      const nfcFinal = nfcSubtotal - nfcDiscountAmount;
      const total = nfcFinal + paperTotal;
      return { 
        basePrice: nfcPrice, 
        quantity, 
        subtotal: nfcSubtotal + paperTotal, 
        discountPercent, 
        discountAmount: nfcDiscountAmount, 
        nfcPrice: nfcFinal,
        paperPrice: paperTotal,
        total, 
        tierName,
        orderType: 'nfc_paper',
        paperQuantity,
        totalPaperCards
      };
    }
  }, [template, quantity, discountTiers, orderType, globalPricing, paperQuantity]);

  const pricing = calculatePricing();

  // Quick Share function
  const handleQuickShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = `${template?.name || 'Business Card'} - Design Preview`;
    const shareText = `Check out this business card design on Piper Card!`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        toast({ title: 'Shared successfully!', description: 'Link has been shared.' });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
        toast({ title: 'Link copied!', description: 'Share link copied to clipboard.' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 2000);
          toast({ title: 'Link copied!', description: 'Share link copied to clipboard.' });
        } catch (e) {
          toast({ title: 'Error', description: 'Failed to share. Please try again.', variant: 'destructive' });
        }
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast({ title: 'Link copied!', description: 'Share link copied to clipboard.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to copy link.', variant: 'destructive' });
    }
  };

  // Generate CSV template based on editable text fields - uses actual text values as headers
  const generateCsvTemplate = () => {
    // Get all editable text element's actual text values from front and back
    const frontFields = (template?.front_text_elements || []).map(el => el.text || el.label || el.id);
    const backFields = (template?.back_text_elements || []).map(el => el.text || el.label || el.id);
    const allFields = [...new Set([...frontFields, ...backFields])].filter(Boolean);
    
    if (allFields.length === 0) {
      allFields.push('Name', 'Title', 'Company', 'Email', 'Phone');
    }
    
    // Create CSV content with headers and one sample row
    const header = allFields.join(',');
    const sampleRow = allFields.map(f => `Sample ${f}`).join(',');
    const csvContent = `${header}\n${sampleRow}`;
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template?.name || 'template'}_bulk_order.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({ title: 'CSV template downloaded', description: 'Fill in your data for each card. Upload is optional - we\'ll contact you if needed.' });
  };

  // Handle CSV upload
  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file', variant: 'destructive' });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast({ title: 'Empty CSV', description: 'No data rows found', variant: 'destructive' });
        return;
      }
      
      // Parse CSV headers and data
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        rows.push(row);
      }
      
      const rowCount = rows.length;
      if (rowCount >= 1) {
        setQuantity(rowCount);
        setCsvFile(file);
        setCsvData(rows);
        toast({ title: 'CSV uploaded', description: `${rowCount} entries detected` });
      } else {
        toast({ title: 'Empty CSV', description: 'No data rows found', variant: 'destructive' });
      }
    };
    reader.readAsText(file);
  };

  // Execute pending action after login
  useEffect(() => {
    if (user && pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      if (action === 'save') handleSaveDesign();
      else if (action === 'cart') handleAddToCart();
    }
  }, [user]);

  // Generate card previews with CSV data merged into text elements
  const generateCardPreviews = useCallback(() => {
    if (!csvData || csvData.length === 0) {
      // If no CSV, generate previews with current text elements
      return Array.from({ length: quantity }, (_, i) => ({
        cardNumber: i + 1,
        frontElements: frontTextElements,
        backElements: backTextElements
      }));
    }
    
    // Map CSV data to text elements
    return csvData.map((row, idx) => {
      // Replace text in elements where the text value matches a CSV header
      const mapElementsWithCsv = (elements) => {
        return elements.map(el => {
          // Check if this element's text matches any CSV header
          const matchingHeader = Object.keys(row).find(
            header => header.toLowerCase() === el.text?.toLowerCase() || 
                      el.text?.toLowerCase().includes(header.toLowerCase())
          );
          if (matchingHeader && row[matchingHeader]) {
            return { ...el, text: row[matchingHeader] };
          }
          return el;
        });
      };
      
      return {
        cardNumber: idx + 1,
        frontElements: mapElementsWithCsv(frontTextElements),
        backElements: mapElementsWithCsv(backTextElements)
      };
    });
  }, [csvData, quantity, frontTextElements, backTextElements]);

  const handleSaveDesign = async () => {
    if (!user) {
      pendingActionRef.current = 'save';
      setShowSignIn(true);
      return;
    }
    try {
      const token = await user.getIdToken();
      const tid = template?.id || templateId;
      const response = await fetch(`${API_URL}/api/card-designer/my-designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          template_id: tid, 
          front_text_elements: frontTextElements, 
          back_text_elements: backTextElements, 
          front_user_logo: frontUserLogo,
          back_user_logo: backUserLogo,
          front_logo_position: frontLogoPosition,
          back_logo_position: backLogoPosition,
          qr_position: qrPosition,
          // Legacy fields for backward compatibility
          user_logo: frontUserLogo,
          user_logo_position: frontLogoPosition
        })
      });
      if (response.ok) {
        toast({ title: "Design saved!", description: "Your customization has been saved" });
      } else { throw new Error('Failed'); }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save design", variant: "destructive" });
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      pendingActionRef.current = 'cart';
      setShowSignIn(true);
      return;
    }
    setAddingToCart(true);
    try {
      const token = await user.getIdToken();
      const tid = template?.id || templateId;
      
      // Save design first
      const response = await fetch(`${API_URL}/api/card-designer/my-designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          template_id: tid, 
          front_text_elements: frontTextElements, 
          back_text_elements: backTextElements,
          front_user_logo: frontUserLogo,
          back_user_logo: backUserLogo,
          front_logo_position: frontLogoPosition,
          back_logo_position: backLogoPosition,
          user_logo: frontUserLogo,
          user_logo_position: frontLogoPosition
        })
      });
      
      if (response.ok) {
        const design = await response.json();
        
        // Upload CSV to Firebase Storage if provided
        let csvUrl = null;
        if (csvFile) {
          const csvPath = `user-designs/${user.uid}/${Date.now()}_bulk_order.csv`;
          csvUrl = await uploadImage(csvFile, csvPath);
        }
        
        // Add to cart with quantity, CSV URL, and paper card preference
        const cartResponse = await fetch(`${API_URL}/api/card-designer/add-to-cart-v2`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            design_id: design.id,
            quantity: quantity,
            csv_url: csvUrl,
            order_type: orderType,  // 'nfc_paper', 'nfc_only', or 'paper_only'
            paper_quantity: paperQuantity  // 100, 200, 300, 500, or 1000
          })
        });
        
        if (!cartResponse.ok) {
          throw new Error('Failed to add to cart');
        }
        
        refreshGlobalCart();
        const orderTypeMsg = orderType === 'paper_only' ? ' (Paper cards only)' : 
                            orderType === 'nfc_only' ? ' (NFC only)' : ' (NFC + Paper cards)';
        toast({ title: "Added to cart!", description: `${quantity} card(s) added${orderTypeMsg}` }); 
        navigate('/cart');
      } else { throw new Error('Failed to save design'); }
    } catch (error) {
      toast({ title: "Error", description: "Failed to add to cart", variant: "destructive" });
    } finally { setAddingToCart(false); }
  };

  if (loading) {
    return (<Layout><div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-10 h-10 text-amber-500 animate-spin" /></div></Layout>);
  }
  if (!template) {
    return (<Layout><div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4"><p className="text-gray-500 mb-4">Template not found</p><Button onClick={() => navigate('/shop')}>Back to Shop</Button></div></Layout>);
  }

  // Generate Product Schema for SEO
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${template.name} - NFC Business Card`,
    "description": `Professional NFC business card design - ${template.name}. Customize and order online. Tap to share contacts instantly.`,
    "image": template.thumbnail_image || template.front_image,
    "brand": {
      "@type": "Brand",
      "name": "Piper Card"
    },
    "offers": {
      "@type": "Offer",
      "price": template.price || 999,
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "Piper Card"
      },
      "priceValidUntil": "2027-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pipercard.com" },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": "https://pipercard.com/shop" },
      { "@type": "ListItem", "position": 3, "name": categorySlug || "Category", "item": `https://pipercard.com/shop/${categorySlug}` },
      { "@type": "ListItem", "position": 4, "name": template.name, "item": `https://pipercard.com/shop/${categorySlug}/${templateSlug}` }
    ]
  };

  // Get current side's logo position
  const logoPosition = getEffectiveLogoPosition(activeSide);
  const currentUserLogo = activeSide === 'front' ? frontUserLogo : backUserLogo;
  const setCurrentUserLogo = activeSide === 'front' ? setFrontUserLogo : setBackUserLogo;
  const currentLogoInputRef = activeSide === 'front' ? frontLogoInputRef : backLogoInputRef;
  const currentLogoPosition = activeSide === 'front' ? frontLogoPosition : backLogoPosition;
  const setCurrentLogoPosition = activeSide === 'front' ? setFrontLogoPosition : setBackLogoPosition;

  // Background for current side
  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    return `${API_URL}${url}`;
  };

  const sideBgColor = activeSide === 'front' ? (template.front_bg_color || template.background_color || '#1e293b') : (template.back_bg_color || '#1e293b');
  const sideBgImage = resolveUrl(activeSide === 'front' ? template.front_bg_image : template.back_bg_image);

  // Render resize handles for logo
  const renderResizeHandles = () => {
    if (!logoPosition) return null;
    const handleSize = 10;
    const corners = ['tl', 'tr', 'bl', 'br'];
    const positions = {
      tl: { top: -handleSize/2, left: -handleSize/2, cursor: 'nw-resize' },
      tr: { top: -handleSize/2, right: -handleSize/2, cursor: 'ne-resize' },
      bl: { bottom: -handleSize/2, left: -handleSize/2, cursor: 'sw-resize' },
      br: { bottom: -handleSize/2, right: -handleSize/2, cursor: 'se-resize' },
    };
    return corners.map(corner => (
      <div
        key={corner}
        className="absolute w-3 h-3 bg-amber-500 border-2 border-white rounded-full shadow-md z-30 hover:scale-125 transition-transform"
        style={{ ...positions[corner], cursor: positions[corner].cursor }}
        onMouseDown={(e) => handleResizeStart(e, corner, activeSide)}
        onTouchStart={(e) => handleResizeStart(e, corner, activeSide)}
      />
    ));
  };

  return (
    <Layout>
      <SEO
        title={`${template.name} - Buy NFC Business Card Online`}
        description={`Customize and buy ${template.name} NFC business card. Professional design, tap to share contacts. Free shipping in India. Starting at ₹${template.price || 999}.`}
        keywords={`${template.name}, NFC business card, digital business card, buy NFC card online, professional visiting card`}
        image={template.thumbnail_image || template.front_image}
        canonicalUrl={`https://pipercard.com/shop/${categorySlug}/${templateSlug}`}
        structuredData={[productSchema, breadcrumbSchema]}
      />
      
      {/* Preview Mode - Shows template with full-screen SEO content */}
      {!isEditorMode ? (
        <div className="min-h-screen bg-gray-50">
          {/* Header - Compact */}
          <div className="bg-gray-900 text-white px-4 py-2.5 sticky top-0 z-50">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <h1 className="text-sm font-medium text-white/90">{template.name}</h1>
              <button 
                onClick={() => setIsEditorMode(true)} 
                className="text-xs font-medium text-amber-400 hover:text-amber-300 transition-colors"
              >
                Edit Design
              </button>
            </div>
          </div>

          {/* Template Preview Section */}
          <div className="max-w-5xl mx-auto px-4 py-6">
            <div className="grid lg:grid-cols-5 gap-6 mb-10">
              {/* Template Image - 3 cols */}
              <div className="lg:col-span-3 space-y-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="aspect-[1.75/1] rounded-lg overflow-hidden bg-gray-50">
                    <img 
                      src={template.thumbnail_image || template.front_image}
                      alt={template.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                
                {/* Price & CTA - Compact */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-500">NFC Card Price</p>
                      <div className="flex items-baseline gap-2">
                        {globalPricing?.nfc_mrp > globalPricing?.nfc_price && (
                          <p className="text-sm text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                        )}
                        <p className="text-2xl font-bold text-gray-900">₹{globalPricing?.nfc_price || template.price || 999}</p>
                      </div>
                      {globalPricing?.nfc_mrp > globalPricing?.nfc_price && (
                        <p className="text-xs text-emerald-600 font-medium">
                          {Math.round(((globalPricing.nfc_mrp - globalPricing.nfc_price) / globalPricing.nfc_mrp) * 100)}% OFF
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
                        Free Shipping
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsEditorMode(true)} 
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white h-10 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Customize This Design
                  </button>
                </div>
              </div>

              {/* Template Info - 2 cols */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sticky top-16">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
                      <p className="text-xs text-gray-500">{template.category_name || 'Business Card'}</p>
                    </div>
                  </div>
                  
                  {template.description && (
                    <div className="mb-4 pb-4 border-b border-gray-100">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">About</h3>
                      <div 
                        className={`description-content text-sm ${!isDescriptionExpanded ? 'line-clamp-3 overflow-hidden' : ''}`}
                        style={{ maxWidth: '100%' }}
                        dangerouslySetInnerHTML={{ __html: template.description }}
                      />
                      {template.description.replace(/<[^>]+>/g, '').length > 200 && (
                        <button 
                          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                          className="text-amber-600 hover:text-amber-700 text-xs font-medium mt-2 inline-flex items-center gap-1"
                        >
                          {isDescriptionExpanded ? 'Read less' : 'Read more'}
                          <svg className={`w-3 h-3 transition-transform ${isDescriptionExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Included</h4>
                    <ul className="space-y-1.5">
                      {['Fully customizable', 'Add your logo', 'High-quality print', 'Bulk discounts'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Paper & NFC Cards Section */}
            <article className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" itemScope itemType="https://schema.org/Product">
              {/* Header */}
              <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-5 py-4">
                <h2 className="text-lg font-semibold mb-1" itemProp="name">
                  NFC Smart Business Cards
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed" itemProp="description">
                  Premium NFC cards with tap-to-share technology for modern networking.
                </p>
              </header>

              {/* Product Info Section */}
              <div className="p-4 md:p-5">
                <div className="max-w-2xl mx-auto">
                  
                  {/* NFC Smart Business Cards */}
                  <section className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                          <path d="M8 12a4 4 0 0 1 8 0"/>
                          <path d="M6 12a6 6 0 0 1 12 0"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">NFC Smart Cards</h3>
                        <p className="text-xs text-blue-600">Tap to Share Instantly</p>
                      </div>
                    </div>
                    
                    {/* Specs Table */}
                    <div className="bg-white/80 rounded-lg overflow-hidden mb-3">
                      <table className="w-full text-xs">
                        <tbody>
                          <tr className="border-b border-blue-100">
                            <td className="py-2 px-3 text-gray-500 font-medium">Size</td>
                            <td className="py-2 px-3 text-gray-900 text-right">85.6 × 54mm (Credit Card)</td>
                          </tr>
                          <tr className="border-b border-blue-100">
                            <td className="py-2 px-3 text-gray-500 font-medium">Material</td>
                            <td className="py-2 px-3 text-gray-900 text-right">Premium PVC Plastic</td>
                          </tr>
                          <tr className="border-b border-blue-100">
                            <td className="py-2 px-3 text-gray-500 font-medium">Finish</td>
                            <td className="py-2 px-3 text-gray-900 text-right">Matte UV Print</td>
                          </tr>
                          <tr className="border-b border-blue-100">
                            <td className="py-2 px-3 text-gray-500 font-medium">Technology</td>
                            <td className="py-2 px-3 text-gray-900 text-right">Built-in NFC Chip</td>
                          </tr>
                          <tr>
                            <td className="py-2 px-3 text-gray-500 font-medium">Delivery</td>
                            <td className="py-2 px-3 text-gray-900 text-right">5-7 Business Days</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Features */}
                    <div className="space-y-1.5 mb-3">
                      {[
                        'One tap to share contact, website, socials',
                        'Works with iPhone & Android (no app needed)',
                        'Waterproof & long-lasting durability'
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                          <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                          {item}
                        </div>
                      ))}
                    </div>

                    {/* Best For */}
                    <div className="pt-3 border-t border-blue-200/50">
                      <p className="text-xs text-gray-500 mb-1.5">Best for:</p>
                      <div className="flex flex-wrap gap-1">
                        {['Tech Savvy', 'Networkers', 'Executives', 'Sales'].map((item, i) => (
                          <span key={i} className="bg-white text-blue-700 px-2 py-0.5 rounded text-xs font-medium border border-blue-200">{item}</span>
                        ))}
                      </div>
                    </div>
                  </section>
                </div>

                {/* Disclaimer */}
                <p className="mt-4 text-center text-xs text-gray-400">
                  Note: We do not accept designs representing government organizations.
                </p>
              </div>
            </article>

            {/* Bottom CTA - Compact */}
            <div className="mt-6 text-center">
              <button 
                onClick={() => setIsEditorMode(true)} 
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white h-9 px-6 text-sm font-medium rounded-lg transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Start Customizing
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Editor Mode - Original Editor UI */
        <div className="min-h-screen bg-gray-100">
          {/* Header */}
          <div className="bg-gray-900 text-white px-4 py-3 sticky top-0 z-50" data-testid="user-editor-header">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setIsEditorMode(false)} className="text-white hover:bg-white/10 p-2" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                {/* Auto-save status indicator */}
                {autoSaveStatus && (
                  <span className={`text-xs flex items-center gap-1 ${autoSaveStatus === 'saving' ? 'text-amber-400' : 'text-green-400'}`}>
                    {autoSaveStatus === 'saving' ? (
                      <><Loader2 className="w-3 h-3 animate-spin" />Saving...</>
                    ) : (
                      <><Save className="w-3 h-3" />Saved</>
                    )}
                  </span>
                )}
                <Button onClick={handleSaveDesign} variant="outline" className="text-sm border-amber-500 text-amber-600 hover:bg-amber-50" size="sm" data-testid="header-save-design-btn">
                  <Save className="w-4 h-4 mr-1.5" />Save
                </Button>
              </div>
              <Button onClick={handleAddToCart} disabled={addingToCart} className="bg-amber-500 hover:bg-amber-600 text-sm" size="sm" data-testid="header-add-to-cart-btn">
                {addingToCart ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShoppingCart className="w-4 h-4 mr-1.5" />Add to Cart</>}
              </Button>
            </div>
          </div>

          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Left: Card Canvas */}
              <div className="space-y-4 w-full overflow-hidden">
                {/* Side Toggle */}
                <div className="flex items-center justify-center gap-2">
                  <div className="flex bg-white rounded-lg p-1 shadow-sm">
                    <button onClick={() => { setActiveSide('front'); setEditingElement(null); }}
                      className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${activeSide === 'front' ? 'bg-amber-500 text-white shadow' : 'text-gray-600'}`}
                      data-testid="side-front-btn">Front</button>
                    <button onClick={() => { setActiveSide('back'); setEditingElement(null); }}
                      className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${activeSide === 'back' ? 'bg-amber-500 text-white shadow' : 'text-gray-600'}`}
                      data-testid="side-back-btn">Back</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetToDefault} className="text-gray-400 hover:text-gray-600" title="Reset template to original design">
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>

              {/* Canvas */}
              <div 
                className="overflow-hidden shadow-xl rounded-xl w-full max-w-full"
                onTouchStart={handleSwipeStart}
                onTouchEnd={handleSwipeEnd}
              >
                <div ref={canvasRef} className="relative select-none" data-testid="user-canvas"
                  style={{
                    aspectRatio: '3.375 / 2.125',
                    backgroundColor: sideBgColor,
                    backgroundImage: sideBgImage ? `url(${sideBgImage})` : 'none',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'default',
                    touchAction: 'pan-y pinch-zoom',
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchMove={handleMouseMove}
                  onTouchEnd={handleMouseUp}
                >
                  {/* Alignment Guide Lines - Show when dragging */}
                  {(isDraggingText || isDragging || isDraggingQr) && (
                    <>
                      {/* Vertical center line */}
                      <div 
                        className="absolute top-0 bottom-0 w-px bg-amber-500 pointer-events-none z-30"
                        style={{ 
                          left: '50%', 
                          opacity: 0.6,
                          boxShadow: '0 0 4px rgba(245, 158, 11, 0.8)'
                        }} 
                      />
                      {/* Horizontal center line */}
                      <div 
                        className="absolute left-0 right-0 h-px bg-amber-500 pointer-events-none z-30"
                        style={{ 
                          top: '50%', 
                          opacity: 0.6,
                          boxShadow: '0 0 4px rgba(245, 158, 11, 0.8)'
                        }} 
                      />
                      {/* Current element position lines */}
                      {isDraggingText && draggingTextId && (() => {
                        const el = textElements.find(t => t.id === draggingTextId);
                        if (!el) return null;
                        return (
                          <>
                            <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                              style={{ left: `${el.x}%`, opacity: 0.7 }} />
                            <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                              style={{ top: `${el.y}%`, opacity: 0.7 }} />
                          </>
                        );
                      })()}
                      {isDragging && logoPosition && (
                        <>
                          <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                            style={{ left: `${logoPosition.x}%`, opacity: 0.7 }} />
                          <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                            style={{ top: `${logoPosition.y}%`, opacity: 0.7 }} />
                        </>
                      )}
                      {isDraggingQr && qrPosition && (
                        <>
                          <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                            style={{ left: `${qrPosition.x}%`, opacity: 0.7 }} />
                          <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                            style={{ top: `${qrPosition.y}%`, opacity: 0.7 }} />
                        </>
                      )}
                    </>
                  )}

                  {/* Logo element with resize handles */}
                  {logoPosition && (() => {
                    const displayLogo = currentUserLogo || resolveUrl(activeSide === 'front' ? template.front_logo_position?.image : template.back_logo_position?.image);
                    const shapeStyle = getLogoShapeStyle(logoPosition.shape || 'circle');
                    
                    if (displayLogo) {
                      return (
                        <div 
                          className="absolute overflow-visible"
                          style={{
                            left: `${logoPosition.x}%`, top: `${logoPosition.y}%`,
                            transform: 'translate(-50%, -50%)', 
                            width: `${logoPosition.size}%`,
                            aspectRatio: logoPosition.shape === 'rounded-rect' ? '1.4/1' : '1',
                          }}
                        >
                          <div 
                            className="relative w-full h-full cursor-grab active:cursor-grabbing group"
                            style={{
                              ...shapeStyle,
                              overflow: 'hidden',
                              boxShadow: (isDragging || isResizing) ? '0 0 0 2px rgba(245, 158, 11, 0.7)' : 'none',
                            }}
                            onMouseDown={(e) => handleDragStart(e, activeSide)}
                            onTouchStart={(e) => handleDragStart(e, activeSide)}
                          >
                            <img src={displayLogo} alt="Logo" className="w-full h-full object-cover" draggable={false} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                          {/* Resize handles */}
                          {renderResizeHandles()}
                        </div>
                      );
                    }
                    
                    // No logo image — show upload placeholder
                    return (
                      <div 
                        className="absolute overflow-visible"
                        style={{
                          left: `${logoPosition.x}%`, top: `${logoPosition.y}%`,
                          transform: 'translate(-50%, -50%)',
                          width: `${logoPosition.size}%`,
                          aspectRatio: logoPosition.shape === 'rounded-rect' ? '1.4/1' : '1',
                        }}
                      >
                        <div 
                          className="relative w-full h-full flex flex-col items-center justify-center cursor-grab active:cursor-grabbing"
                          style={{
                            border: '2px dashed rgba(212,175,55,0.4)',
                            ...shapeStyle,
                            backgroundColor: 'rgba(212,175,55,0.06)',
                            boxShadow: (isDragging || isResizing) ? '0 0 0 2px rgba(245, 158, 11, 0.7)' : 'none',
                          }}
                          onMouseDown={(e) => handleDragStart(e, activeSide)}
                          onTouchStart={(e) => handleDragStart(e, activeSide)}
                          onClick={(e) => { if (!isDragging && !isResizing) currentLogoInputRef.current?.click(); }}
                          data-testid="logo-placeholder"
                        >
                          <Upload className="w-4 h-4 text-amber-500/50" />
                          <span className="text-[10px] font-medium text-amber-500/50 uppercase tracking-wider">Logo</span>
                        </div>
                        {/* Resize handles */}
                        {renderResizeHandles()}
                      </div>
                    );
                  })()}

                  {/* QR code placeholder on back side - DRAGGABLE */}
                  {activeSide === 'back' && qrPosition && (() => {
                    const qr = qrPosition;
                    const color = qr.color || '#ffffff';
                    const isBeingDragged = isDraggingQr;
                    return (
                      <div 
                        className={`absolute flex flex-col items-center justify-center cursor-grab active:cursor-grabbing ${isBeingDragged ? 'ring-2 ring-amber-500 rounded-lg' : 'hover:ring-2 hover:ring-amber-400/50 hover:rounded-lg'}`}
                        style={{
                          left: `${qr.x}%`, top: `${qr.y}%`,
                          transform: 'translate(-50%, -50%)',
                          width: `${qr.size}%`, aspectRatio: '1',
                          zIndex: isBeingDragged ? 25 : 5,
                        }}
                        onMouseDown={handleQrDragStart}
                        onTouchStart={handleQrDragStart}
                        data-testid="qr-draggable"
                      >
                        <div className="w-full aspect-square rounded-lg flex items-center justify-center"
                          style={{ border: `2px dashed ${color}50`, backgroundColor: `${color}12` }}>
                          <svg viewBox="0 0 24 24" className="w-1/2 h-1/2" fill="none" stroke={color} strokeWidth="1.5" style={{ opacity: 0.5 }}>
                            <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /><rect x="18" y="18" width="3" height="3" />
                          </svg>
                        </div>
                        <span className="text-[9px] mt-1" style={{ color: `${color}70` }}>pipercard.com</span>
                        {/* Drag indicator */}
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full opacity-70 flex items-center justify-center" title="Drag to move">
                          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z"/></svg>
                        </span>
                      </div>
                    );
                  })()}

                  {/* Editable text elements - ALL are now movable */}
                  {textElements.map(el => {
                    const scaledSize = el.fontSize * canvasScale;
                    const isBeingDragged = isDraggingText && draggingTextId === el.id;
                    const alignment = el.textAlign || 'center';
                    // Adjust transform based on text alignment
                    // Left: anchor from left edge (translateX: 0), Center: anchor from center (-50%), Right: anchor from right edge (-100%)
                    const transformX = alignment === 'left' ? '0%' : alignment === 'right' ? '-100%' : '-50%';
                    return (
                    <div key={el.id}
                      className="absolute"
                      style={{
                        left: `${el.x}%`, top: `${el.y}%`, 
                        transform: `translate(${transformX}, -50%)`,
                        zIndex: editingElement === el.id || isBeingDragged ? 20 : 1,
                      }}>
                      {editingElement === el.id ? (
                        <div style={{
                          display: 'flex',
                          justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
                        }}>
                          <input
                            autoFocus
                            value={el.text}
                            style={{
                              fontSize: `${scaledSize}pt`, 
                              fontFamily: el.fontFamily || 'Inter',
                              fontWeight: el.fontWeight || 'normal', 
                              color: el.color || '#FFFFFF',
                              textAlign: alignment,
                              width: 'auto',
                              minWidth: '50px',
                              maxWidth: '500px',
                              background: 'transparent',
                              border: 'none',
                              outline: 'none',
                              padding: '0',
                              margin: '0',
                              caretColor: el.color || '#FFFFFF',
                              direction: alignment === 'right' ? 'rtl' : 'ltr',
                            }}
                            onChange={(e) => {
                              updateTextContent(el.id, e.target.value);
                            }}
                            onBlur={() => setEditingElement(null)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            data-testid={`edit-input-${el.id}`}
                          />
                        </div>
                      ) : (
                        <div
                          className={`transition-all cursor-grab active:cursor-grabbing ${isBeingDragged ? 'ring-2 ring-amber-500' : 'hover:ring-2 hover:ring-amber-400/50'}`}
                          style={{
                            fontSize: `${scaledSize}pt`, fontFamily: el.fontFamily || 'Inter',
                            fontWeight: el.fontWeight || 'normal', color: el.color || '#FFFFFF',
                            textAlign: alignment, whiteSpace: 'nowrap',
                            userSelect: 'none',
                          }}
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (!isDraggingText) handleTextClick(e, el.id); 
                          }}
                          onMouseDown={(e) => handleTextDragStart(e, el)}
                          onTouchStart={(e) => handleTextDragStart(e, el)}
                          data-testid={`user-text-${el.id}`}
                        >
                          {el.text}
                          {/* Move indicator on all text elements */}
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full opacity-50" title="Drag to move" />
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 px-2">Click text to edit • Drag to move • Swipe to flip</p>
            </div>

            {/* Right: Logo controls + cart */}
            <div className="space-y-4 w-full overflow-hidden">
              {/* Logo upload & controls - shows for current side */}
              {logoPosition && (
              <Card className="shadow-md">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"><Upload className="w-4 h-4 text-blue-600" /></div>
                      <h3 className="font-semibold text-gray-800">{activeSide === 'front' ? 'Front' : 'Back'} Logo</h3>
                    </div>
                  </div>
                  <input ref={currentLogoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, activeSide)} />
                  
                  {/* Logo preview & upload - available for all users */}
                  {currentUserLogo ? (
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <img src={currentUserLogo} alt="Logo" className="w-16 h-16 object-contain border bg-white p-1" 
                          style={{ ...getLogoShapeStyle(logoPosition.shape || 'circle') }} />
                        <button onClick={() => setCurrentUserLogo(null)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow"><X className="w-3 h-3" /></button>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => currentLogoInputRef.current?.click()}>Change</Button>
                    </div>
                  ) : (
                    <Button variant="outline" className="w-full h-16 border-dashed border-2 hover:border-amber-400 mb-4" onClick={() => currentLogoInputRef.current?.click()} data-testid="upload-logo-btn">
                      <div className="text-center"><Upload className="w-5 h-5 mx-auto mb-1 text-gray-400" /><span className="text-xs text-gray-500">Upload {activeSide} logo</span></div>
                    </Button>
                  )}

                  {/* Logo Shape */}
                  <div className="mb-4">
                    <Label className="text-xs text-gray-600 mb-1.5 block">Shape</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {LOGO_SHAPES.map(s => (
                        <button 
                          key={s.value}
                          className={`px-2 py-2 text-xs rounded-lg border-2 transition-all ${
                            (logoPosition.shape || 'circle') === s.value 
                              ? 'border-amber-500 bg-amber-50 font-semibold text-amber-700' 
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                          onClick={() => setCurrentLogoPosition(prev => ({ ...logoPosition, ...prev, shape: s.value }))}
                          data-testid={`logo-shape-${s.value}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Size indicator */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                    <span>Size: {Math.round(logoPosition.size)}%</span>
                    <span className="text-amber-600">Drag corners to resize</span>
                  </div>

                  {/* Reset button */}
                  {currentLogoPosition && (
                    <Button variant="ghost" size="sm" onClick={() => resetLogoToDefault(activeSide)} className="text-xs text-gray-500 hover:text-gray-700 w-full">
                      <RotateCcw className="w-3 h-3 mr-1" /> Reset {activeSide} logo to default
                    </Button>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Price & Cart */}
              <Card className="shadow-md bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
                <CardContent className="p-5">
                  {/* Quantity & Price Section - Side by Side */}
                  <div className="flex items-start justify-between gap-4 mb-4 p-4 bg-white/80 rounded-xl border border-amber-200">
                    {/* Left: Quantity Selector */}
                    <div>
                      <Label className="text-xs text-gray-600 mb-2 block">Quantity</Label>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => { setQuantity(Math.max(1, quantity - 1)); if (quantity <= 2) setCsvFile(null); }} disabled={quantity <= 1}>
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input 
                          type="number" 
                          min={1} 
                          value={quantity} 
                          onChange={e => { const v = Math.max(1, parseInt(e.target.value) || 1); setQuantity(v); if (v <= 1) setCsvFile(null); }} 
                          className="w-16 h-9 text-center text-lg font-bold" 
                        />
                        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setQuantity(quantity + 1)}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Right: Full Price Breakdown */}
                    <div className="text-right min-w-[120px]">
                      <div className="text-xs text-gray-500 mb-1">
                        NFC Card ({quantity}x ₹{pricing.basePrice})
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        ₹{pricing.subtotal?.toLocaleString()}
                      </div>
                      {pricing.discountPercent > 0 && (
                        <div className="text-xs text-green-600 mb-1">
                          -{pricing.discountPercent}% (₹{pricing.discountAmount?.toLocaleString()})
                        </div>
                      )}
                      <div className="pt-2 border-t border-amber-200">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-2xl font-bold text-amber-600" data-testid="template-total-header">₹{pricing.total?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Discounts */}
                  {discountTiers.length > 0 && (
                    <div className="bg-white/60 rounded-lg p-3 mb-4">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Bulk Discounts</p>
                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5">
                        {discountTiers.filter(t => t.discount_percent > 0).map((tier, i) => (
                          <span 
                            key={i} 
                            className={`px-2 py-1 rounded-full text-xs font-medium text-center ${
                              pricing.tierName === (tier.max_quantity ? `${tier.min_quantity}-${tier.max_quantity}` : `${tier.min_quantity}+`) 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'}: {tier.discount_percent}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CSV Upload for Bulk Orders */}
                  {quantity > 1 && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Bulk Order Data</span>
                      </div>
                      <p className="text-xs text-blue-600 mb-3">Upload CSV with personalized data for each card</p>
                      
                      {/* Download Template Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={generateCsvTemplate}
                        className="w-full mb-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        <Download className="w-3 h-3 mr-1.5" />
                        Download CSV Template
                      </Button>
                      
                      {/* Upload CSV - Optional */}
                      <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
                      {csvFile ? (
                        <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 mb-2">
                          <div className="flex items-center gap-2 text-sm text-green-700">
                            <FileSpreadsheet className="w-4 h-4" />
                            <span className="truncate max-w-[120px]">{csvFile.name}</span>
                            <span className="text-xs text-gray-500">({quantity} rows)</span>
                          </div>
                          <button onClick={() => { setCsvFile(null); setCsvData([]); }} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => csvInputRef.current?.click()}
                          className="w-full mb-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          <Upload className="w-3 h-3 mr-1.5" />
                          Upload CSV (Optional)
                        </Button>
                      )}
                      
                      {/* Preview All Cards Button */}
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => setShowPreviewModal(true)}
                        className="w-full text-xs bg-violet-600 hover:bg-violet-700"
                        data-testid="preview-all-cards-btn"
                      >
                        <Eye className="w-3 h-3 mr-1.5" />
                        Preview All {quantity} Cards
                      </Button>
                      
                      <p className="text-xs text-blue-600 mt-2 text-center">CSV is optional. We'll contact you after payment if needed.</p>
                    </div>
                  )}

                  {/* Quick Share Section - Bio Style */}
                  <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 mb-4 border border-violet-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Share2 className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-semibold text-violet-800">Quick Share</span>
                    </div>
                    <p className="text-xs text-violet-600 mb-3">Share this design with others</p>
                    
                    {/* Share Link Input */}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1 bg-white rounded-lg border border-violet-200 px-3 py-2 text-xs text-gray-600 truncate">
                        {typeof window !== 'undefined' ? window.location.href : ''}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyLink}
                        className="px-3 border-violet-300 text-violet-600 hover:bg-violet-100"
                        data-testid="copy-link-btn"
                      >
                        {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    
                    {/* Share Button */}
                    <Button
                      onClick={handleQuickShare}
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white h-10 text-sm font-medium"
                      data-testid="quick-share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Design
                    </Button>
                  </div>

                  <Button onClick={handleSaveDesign} variant="outline" className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 h-10 text-sm font-semibold mb-3" data-testid="save-design-btn">
                    <Save className="w-4 h-4 mr-2" />Save Design
                  </Button>
                  <Button onClick={handleAddToCart} disabled={addingToCart} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12 text-base font-semibold" data-testid="add-to-cart-btn">
                    {addingToCart ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShoppingCart className="w-5 h-5 mr-2" />Add to Cart</>}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      )}
      
      {/* Preview All Cards Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowPreviewModal(false)}>
          <div 
            className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-violet-600 to-purple-600">
              <div className="text-white">
                <h2 className="text-lg font-bold">Preview All {quantity} Cards</h2>
                <p className="text-sm text-violet-200">
                  {csvData.length > 0 ? 'Showing cards with your CSV data' : 'Showing cards with current design'}
                </p>
              </div>
              <button 
                onClick={() => setShowPreviewModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Disclaimer Banner */}
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <Eye className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Preview Only</p>
                  <p className="text-xs text-amber-700">
                    This is a preview of your cards. Our design expert will review your order and take your approval before printing.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Modal Content - Scrollable List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-100">
              <div className="space-y-6 max-w-4xl mx-auto">
                {generateCardPreviews().map((card, idx) => {
                  // Calculate preview scale (preview card is ~300px wide, main canvas is ~500px)
                  const previewScale = 0.6;
                  
                  return (
                  <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 flex items-center justify-between">
                      <span className="text-white font-semibold">Card #{card.cardNumber}</span>
                      <span className="text-violet-200 text-sm">Preview</span>
                    </div>
                    
                    {/* Front and Back Side by Side on Desktop, Stacked on Mobile */}
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
                        {/* Front Preview */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-gray-700">Front Side</p>
                          </div>
                          <div 
                            className="relative rounded-lg overflow-hidden shadow-md border border-gray-200"
                            style={{ 
                              aspectRatio: '3.375 / 2.125',
                              backgroundColor: template?.front_bg_color || '#1e293b',
                              backgroundImage: template?.front_bg_image ? `url(${template.front_bg_image})` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          >
                            {/* Logo on Front */}
                            {(() => {
                              const logoImage = frontUserLogo || template?.front_logo_image || template?.front_logo_position?.image;
                              const pos = frontLogoPosition || template?.front_logo_position || template?.logo_placeholder;
                              
                              if (!logoImage && !pos) return null;
                              
                              return (
                                <div 
                                  className="absolute overflow-hidden"
                                  style={{
                                    left: `${pos?.x || 50}%`,
                                    top: `${pos?.y || 30}%`,
                                    width: `${pos?.size || 15}%`,
                                    aspectRatio: pos?.shape === 'rounded-rect' ? '1.4/1' : '1',
                                    transform: 'translate(-50%, -50%)',
                                    ...(pos ? getLogoShapeStyle(pos.shape || 'circle') : { borderRadius: '50%' })
                                  }}
                                >
                                  {logoImage && (
                                    <img 
                                      src={logoImage} 
                                      alt="Logo" 
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              );
                            })()}
                            {/* Front Text Elements */}
                            {card.frontElements.map((el) => {
                              const align = el.textAlign || 'center';
                              const transformX = align === 'left' ? '0%' : align === 'right' ? '-100%' : '-50%';
                              const scaledSize = (el.fontSize || 16) * previewScale;
                              
                              return (
                                <div
                                  key={el.id}
                                  className="absolute whitespace-nowrap"
                                  style={{
                                    left: `${el.x}%`,
                                    top: `${el.y}%`,
                                    transform: `translate(${transformX}, -50%)`,
                                    color: el.color || '#ffffff',
                                    fontSize: `${scaledSize}pt`,
                                    fontFamily: el.fontFamily || 'Inter',
                                    fontWeight: el.fontWeight || 'normal',
                                    fontStyle: el.fontStyle || 'normal',
                                    textAlign: align
                                  }}
                                >
                                  {el.text}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Back Preview */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-gray-700">Back Side</p>
                          </div>
                          <div 
                            className="relative rounded-lg overflow-hidden shadow-md border border-gray-200"
                            style={{ 
                              aspectRatio: '3.375 / 2.125',
                              backgroundColor: template?.back_bg_color || '#1e293b',
                              backgroundImage: template?.back_bg_image ? `url(${template.back_bg_image})` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          >
                            {/* Logo on Back */}
                            {(() => {
                              const logoImage = backUserLogo || template?.back_logo_image || template?.back_logo_position?.image;
                              const pos = backLogoPosition || template?.back_logo_position;
                              
                              if (!logoImage && !pos) return null;
                              
                              return (
                                <div 
                                  className="absolute overflow-hidden"
                                  style={{
                                    left: `${pos?.x || 50}%`,
                                    top: `${pos?.y || 30}%`,
                                    width: `${pos?.size || 15}%`,
                                    aspectRatio: pos?.shape === 'rounded-rect' ? '1.4/1' : '1',
                                    transform: 'translate(-50%, -50%)',
                                    ...(pos ? getLogoShapeStyle(pos.shape || 'circle') : { borderRadius: '50%' })
                                  }}
                                >
                                  {logoImage && (
                                    <img 
                                      src={logoImage} 
                                      alt="Logo" 
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              );
                            })()}
                            {/* QR Code placeholder */}
                            {qrPosition && (
                              <div
                                className="absolute flex items-center justify-center bg-white rounded"
                                style={{
                                  left: `${qrPosition.x}%`,
                                  top: `${qrPosition.y}%`,
                                  width: `${qrPosition.size}%`,
                                  aspectRatio: '1',
                                  transform: 'translate(-50%, -50%)',
                                  padding: '4px',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}
                              >
                                <div className="w-full h-full bg-gray-100 rounded flex items-center justify-center border border-gray-200">
                                  <span className="text-[10px] text-gray-500 font-medium">QR CODE</span>
                                </div>
                              </div>
                            )}
                            {/* Back Text Elements */}
                            {card.backElements.map((el) => {
                              const align = el.textAlign || 'center';
                              const transformX = align === 'left' ? '0%' : align === 'right' ? '-100%' : '-50%';
                              const scaledSize = (el.fontSize || 14) * previewScale;
                              
                              return (
                                <div
                                  key={el.id}
                                  className="absolute whitespace-nowrap"
                                  style={{
                                    left: `${el.x}%`,
                                    top: `${el.y}%`,
                                    transform: `translate(${transformX}, -50%)`,
                                    color: el.color || '#ffffff',
                                    fontSize: `${scaledSize}pt`,
                                    fontFamily: el.fontFamily || 'Inter',
                                    fontWeight: el.fontWeight || 'normal',
                                    fontStyle: el.fontStyle || 'normal',
                                    textAlign: align
                                  }}
                                >
                                  {el.text}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t bg-white flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                Close
              </Button>
              <Button onClick={() => { setShowPreviewModal(false); }} className="bg-violet-600 hover:bg-violet-700">
                Looks Good!
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <SignInModal open={showSignIn} onOpenChange={setShowSignIn} noRedirect />
    </Layout>
  );
};

export default UserTemplateEditor;
